import React, { useContext, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { Rotation } from '../types/ViewModel/Rotation';
import { IRotationNode } from '../types/ViewModel/IRotationNode';
import { Port } from '../types/ViewModel/Port';
import { PortCall } from '../types/ViewModel/PortCall';
import { Berth } from '../types/ViewModel/Berth';
import { CargoListItem } from '../types/ViewModel/CargoListItem';
import { OrcaCall } from '../types/ViewModel/OrcaCall';
import { PortName } from 'src/types/ViewModel/Stowage/Port/PortName';
import { BerthName } from 'src/types/ViewModel/Stowage/Port/BerthName';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { Product } from 'src/types/ViewModel/Stowage/Product/Product';
import { ProductCategory } from 'src/types/ViewModel/Stowage/Product/ProductCategory';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { Coating } from 'src/types/ViewModel/Vessel/Coating';
import { CoatingCompatibility } from 'src/types/ViewModel/Stowage/Product/CoatingCompatibility';
import { UscgCompatibilitiesBM } from 'src/types/BusinessModel/Stowage/Product/UscgCompatibility/UscgCompatibilitiesBM';
import { UUID } from 'crypto';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { TimeZoneData } from 'src/types/ViewModel/TimeZoneData';

type RotationContextType = {
    rotation: Rotation | null;
    setRotation: (rotation: Rotation | null) => void;
    updatedRotation: Rotation | null;
    setUpdatedRotation: (rotation: Rotation | null) => void;
    lastUpdatedUtc: Date | null;
    setLastUpdatedUtc: (date: Date | null) => void;
    selectedNode: IRotationNode | null;
    setSelectedNode: (node: IRotationNode | null) => void;
    selectedPort: Port | null;
    setSelectedPort: (port: Port) => void;
    selectedPortCall: PortCall | null;
    setSelectedPortCall: (portCall: PortCall | null) => void;
    nextPortCall: PortCall | null;
    setCurrentPortCall: (portCall: PortCall | null) => void;
    previousPortCall: PortCall | null;
    setNextPortCall: (portCall: PortCall | null) => void;
    currentPortCall: PortCall | null;
    setPreviousPortCall: (portCall: PortCall | null) => void;
    selectedPortCallGuid: UUID | null;
    setSelectedPortCallGuid: (guid: UUID) => void;
    selectedBerthCallGuid: UUID | null;
    setSelectedBerthCallGuid: (guid: UUID) => void;
    selectedCargoOpGuid: UUID | null;
    setSelectedCargoOpGuid: (guid: UUID) => void;
    unlockedBerthCalls: number[];
    setUnlockedBerthCalls: (unlockedBerthCalls: number[]) => void;
    isRotationVisible: boolean | null;
    setIsRotationVisible: (status: boolean | null) => void;
    // selectedVoyages: string[] | null;
    // setSelectedVoyages: (voyageList: string[] | null) => void;
    selectedStowageVoyages: string[] | null;
    setSelectedStowageVoyages: (voyageList: string[] | null) => void;
    isRotationFilterActive: boolean | null;
    setIsRotationFilterActive: (active: boolean | null) => void;
    isFilterPanelOpen: boolean | null;
    setIsFilterPanelOpen: (status: boolean | null) => void;
    isRotationTreeToBeUpdated: boolean;
    setIsRotationTreeToBeUpdated: (updated: boolean) => void;
    unknownBerth: Berth | null;
    setUnknownBerth: (berth: Berth | null) => void;
    unknownBerthList: Berth[];
    setUnknownBerthList: (berthList: Berth[]) => void;
    portInPortClusterList: Port[];
    setPortInPortClusterList: (portInPortClusterList: Port[]) => void;
    cargoList: CargoListItem[];
    setCargoList: (cargoList: CargoListItem[]) => void;
    orcaCalls: OrcaCall[];
    setOrcaCalls: (orcaCalls: OrcaCall[]) => void;
    portNameList: PortName[] | null;
    setPortNameList: (portNames: PortName[] | null) => void;
    berthNameList: BerthName[] | null;
    setBerthNameList: (berthNames: BerthName[] | null) => void;
    productList: Product[] | null;
    setProductList: (products: Product[] | null) => void;
    productCategoryList: ProductCategory[] | null;
    setProductCategoryList: (productCategories: ProductCategory[] | null) => void;
    coatingList: Coating[] | null;
    setCoatingList: (coatings: Coating[] | null) => void;
    coatingCompatibilities: CoatingCompatibility[] | null;
    setCoatingCompatibilities: (coatingCompatibilities: CoatingCompatibility[] | null) => void;
    uscgCompatibilities: UscgCompatibilitiesBM | null;
    setUscgCompatibilities: (uscgCompatibilities: UscgCompatibilitiesBM | null) => void;
    timeZoneData: TimeZoneData | null;
    setTimeZoneData: (timeZoneData: TimeZoneData | null) => void;
    stowageRotationTree: RotationPortCall[] | null;
    setStowageRotationTree: (rotationPortCalls: RotationPortCall[] | null) => void;
    selectedStowageRotationPortCall: RotationPortCall;
    setSelectedStowageRotationPortCall: (rotationPortCall: RotationPortCall | null) => void;
    selectedStowageRotationBerthCall: RotationBerthCall;
    setSelectedStowageRotationBerthCall: (rotationBerthCall: RotationBerthCall | null) => void;
    operationListExpanded: boolean | null;
    setOperationListExpanded: (expanded: boolean | null) => void;
    imosRotationPortCallList: RotationPortCall[] | null;
    setImosRotationPortCallList: (rotationPortCalls: RotationPortCall[] | null) => void;
    imosRotationCargoList: RotationCargo[] | null;
    setImosRotationCargoList: (rotationCargoes: RotationCargo[] | null) => void;
    stowagePlanRotationPortCallList: RotationPortCall[] | null;
    setStowagePlanRotationPortCallList: (rotationPortCalls: RotationPortCall[] | null) => void;
    stowagePlanRotationCargoList: RotationCargo[] | null;
    setStowagePlanRotationCargoList: (rotationCargoes: RotationCargo[] | null) => void;
    legacyVoyagesLoaded: boolean;
    setLegacyVoyagesLoaded: (loaded: boolean) => void;
};

export const RotationContext = React.createContext<RotationContextType>(undefined!);

export const RotationProvider = ({ children }: ContextProps) => {
    const [rotation, setRotation] = useState<Rotation | null>(null);
    const [updatedRotation, setUpdatedRotation] = useState<Rotation | null>(null);
    const [lastUpdatedUtc, setLastUpdatedUtc] = useState<Date | null>(null);
    const [selectedNode, setSelectedNode] = useState<IRotationNode | null>(null);
    const [selectedPort, setSelectedPort] = useState<Port | null>(null);
    // const [selectedVoyages, setSelectedVoyages] = useState<string[] | null>(null);
    const [selectedStowageVoyages, setSelectedStowageVoyages] = useState<string[] | null>(null);
    const [isRotationFilterActive, setIsRotationFilterActive] = useState<boolean | null>(false);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean | null>(null);
    const [selectedPortCall, setSelectedPortCall] = useState<PortCall | null>(null);
    const [currentPortCall, setCurrentPortCall] = useState<PortCall | null>(null);
    const [nextPortCall, setNextPortCall] = useState<PortCall | null>(null);
    const [previousPortCall, setPreviousPortCall] = useState<PortCall | null>(null);
    const [selectedPortCallGuid, setSelectedPortCallGuid] = useState<UUID | null>(null);
    const [selectedBerthCallGuid, setSelectedBerthCallGuid] = useState<UUID | null>(null);
    const [selectedCargoOpGuid, setSelectedCargoOpGuid] = useState<UUID | null>(null);
    const [unlockedBerthCalls, setUnlockedBerthCalls] = useState<number[]>([]);
    const [isRotationVisible, setIsRotationVisible] = useState<boolean | null>(null);
    const [isRotationTreeToBeUpdated, setIsRotationTreeToBeUpdated] = useState<boolean>(false);
    const [unknownBerth, setUnknownBerth] = useState<Berth | null>(null);
    const [unknownBerthList, setUnknownBerthList] = useState<Berth[]>([]);
    const [portInPortClusterList, setPortInPortClusterList] = useState<Port[]>([]);
    const [cargoList, setCargoList] = useState<CargoListItem[]>([]);
    const [orcaCalls, setOrcaCalls] = useState<OrcaCall[]>([]);
    const [portNameList, setPortNameList] = useState<PortName[] | null>(null);
    const [berthNameList, setBerthNameList] = useState<BerthName[] | null>(null);
    const [productList, setProductList] = useState<Product[] | null>(null);
    const [productCategoryList, setProductCategoryList] = useState<ProductCategory[] | null>(null);
    const [coatingList, setCoatingList] = useState<Coating[] | null>(null);
    const [coatingCompatibilities, setCoatingCompatibilities] = useState<CoatingCompatibility[] | null>(null);
    const [uscgCompatibilities, setUscgCompatibilities] = useState<UscgCompatibilitiesBM | null>(null);
    const [timeZoneData, setTimeZoneData] = useState<TimeZoneData | null>(null);
    const [stowageRotationTree, setStowageRotationTree] = useState<RotationPortCall[] | null>(null);
    const [selectedStowageRotationPortCall, setSelectedStowageRotationPortCall] = useState<RotationPortCall | null>(null);
    const [operationListExpanded, setOperationListExpanded] = useState<boolean | null>(null);
    const [selectedStowageRotationBerthCall, setSelectedStowageRotationBerthCall] = useState<RotationBerthCall | null>(null);
    const [stowagePlanRotationPortCallList, setStowagePlanRotationPortCallList] = useState<RotationPortCall[] | null>(null);
    const [stowagePlanRotationCargoList, setStowagePlanRotationCargoList] = useState<RotationCargo[] | null>(null);
    const [imosRotationPortCallList, setImosRotationPortCallList] = useState<RotationPortCall[] | null>(null);
    const [imosRotationCargoList, setImosRotationCargoList] = useState<RotationCargo[] | null>(null);
    const [legacyVoyagesLoaded, setLegacyVoyagesLoaded] = useState<boolean>(false);

    return (
        <RotationContext.Provider
            value={{
                rotation,
                setRotation,
                updatedRotation,
                setUpdatedRotation,
                lastUpdatedUtc,
                setLastUpdatedUtc,
                selectedNode,
                setSelectedNode,
                selectedPort,
                setSelectedPort,
                currentPortCall,
                setCurrentPortCall,
                nextPortCall,
                setNextPortCall,
                selectedPortCall,
                setSelectedPortCall,
                selectedPortCallGuid,
                setSelectedPortCallGuid,
                selectedBerthCallGuid,
                setSelectedBerthCallGuid,
                selectedCargoOpGuid,
                setSelectedCargoOpGuid,
                unlockedBerthCalls,
                setUnlockedBerthCalls,
                isRotationVisible,
                setIsRotationVisible,
                // selectedVoyages,
                // setSelectedVoyages,
                selectedStowageVoyages,
                setSelectedStowageVoyages,
                isRotationFilterActive,
                setIsRotationFilterActive,
                isFilterPanelOpen,
                setIsFilterPanelOpen,
                isRotationTreeToBeUpdated,
                setIsRotationTreeToBeUpdated,
                unknownBerth,
                setUnknownBerth,
                unknownBerthList,
                setUnknownBerthList,
                portInPortClusterList,
                setPortInPortClusterList,
                previousPortCall,
                setPreviousPortCall,
                cargoList,
                setCargoList,
                orcaCalls,
                setOrcaCalls,
                portNameList,
                setPortNameList,
                berthNameList,
                setBerthNameList,
                productList,
                setProductList,
                productCategoryList,
                setProductCategoryList,
                coatingList,
                setCoatingList,
                coatingCompatibilities,
                setCoatingCompatibilities,
                uscgCompatibilities,
                setUscgCompatibilities,
                timeZoneData,
                setTimeZoneData,
                stowageRotationTree,
                setStowageRotationTree,
                selectedStowageRotationPortCall,
                setSelectedStowageRotationPortCall,
                operationListExpanded,
                setOperationListExpanded,
                selectedStowageRotationBerthCall,
                setSelectedStowageRotationBerthCall,
                imosRotationPortCallList,
                setImosRotationPortCallList,
                imosRotationCargoList,
                setImosRotationCargoList,
                stowagePlanRotationPortCallList,
                setStowagePlanRotationPortCallList,
                stowagePlanRotationCargoList,
                setStowagePlanRotationCargoList,
                legacyVoyagesLoaded,
                setLegacyVoyagesLoaded,
            }}
        >
            {children}
        </RotationContext.Provider>
    );
};

export const useRotationStore = () => useContext(RotationContext);
